import { type FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import Banner from '@/components/UI/Banner';
import { ModalContainer, ModalHeader, ModalActions } from '@/utils/modal';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';
import { errorToast, successToast } from '@/helpers';
import { validateViewName, VIEW_NAME_MAX_LENGTH } from '@/utils/userTableSettings/UserTableSettings.utils';
import styles from './SaveViewModal.module.scss';
import type { UserTableViewSettings, UserTableViewTableName } from '@/model/UserTableView.model.ts';

export interface SaveViewModalProps {
  tableName: UserTableViewTableName;
  settings: UserTableViewSettings;
  onViewSaved: (settings: UserTableViewSettings) => void;
  closeModal: () => void;
}

interface SaveViewFormData {
  viewName: string;
}

const SaveViewModal: FC<SaveViewModalProps> = ({ tableName, settings, onViewSaved, closeModal }) => {
  const { createView, getViews } = useUserTableViews();
  const [isSaving, setIsSaving] = useState(false);

  const views = getViews(tableName);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SaveViewFormData>({
    mode: 'onChange',
    defaultValues: { viewName: '' },
  });

  const onSubmit = async (data: SaveViewFormData) => {
    setIsSaving(true);

    const newView = await createView(tableName, data.viewName.trim(), settings);

    if (newView !== null) {
      onViewSaved(newView.settings);
      closeModal();
      successToast(`Table view '${newView.name}' saved`);
    } else {
      errorToast('Failed to save view. Please try again.');
      setIsSaving(false);
    }
  };

  const fieldError = errors.viewName?.message;

  return (
    <ModalContainer size="narrow" data-test-component="SaveViewModal">
      <ModalHeader title="Save view" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pa3">
          <Banner info dismiss={false} className="mb3 f6" testId="save-view-modal__hint">
            This will save your current columns, sort, page size, and filters.
          </Banner>

          <div className={styles.inputGroup}>
            <label htmlFor="view-name" className={styles.label}>
              View name
            </label>
            <input
              id="view-name"
              type="text"
              className={fieldError ? 'input__error' : ''}
              placeholder="Enter a name for this view..."
              maxLength={VIEW_NAME_MAX_LENGTH}
              autoFocus
              data-test-component="SaveViewModal"
              data-test-element="name-input"
              {...register('viewName', { validate: (value) => validateViewName(value, views) })}
            />
            {fieldError && (
              <small className="red db" data-test-component="SaveViewModal" data-test-element="error">
                {fieldError}
              </small>
            )}
          </div>
        </div>

        <ModalActions
          submitBtnText="Save view"
          submitButtonProps={{
            disabled: isSaving || !isValid,
            loading: isSaving,
          }}
          onSubmit={handleSubmit(onSubmit)}
          onCancel={closeModal}
          className="pa3 bt b--moon-gray"
        />
      </form>
    </ModalContainer>
  );
};

export default SaveViewModal;
